import { assetLogo } from "../../constant";
import { formatAmount } from "../../utils/FormatPerson";

export type BalanceByAsset = {
  asset?: string;
  count?: number;
  totalUSDT?: number;
  totalNGN?: number;
};

export type BalanceByCurrency = {
  currency?: string;
  count?: number;
  totalAmount?: number;
  totalUSDT?: number;
  totalNGN?: number;
};

export type BalanceByPair = {
  from?: string;
  to?: string;
  count?: number;
};

export type BalanceByType = {
  type?: string;
  count?: number;
  totalNGN?: number;
};

export type BalanceByCard = {
  card?: string;
  count?: number;
};

export type BalanceBySource = {
  source?: string | null;
  count?: number;
  totalNGN?: number;
};

export type BalanceByStatus = {
  status?: string;
  count?: number;
  totalNGN?: number;
};

export type UserBalanceSummary = {
  totalTransactions?: number;
  currentBalances?: Record<string, unknown>;
  firstTransaction?: string | null;
  lastTransaction?: string | null;
  sells?: {
    count?: number;
    totalUSDT?: number;
    totalNGN?: number;
    byAsset?: BalanceByAsset[];
  };
  sellDeposits?: {
    count?: number;
    totalUSDT?: number;
    totalNGN?: number;
    byCurrency?: BalanceByCurrency[];
  };
  cryptoDeposits?: {
    count?: number;
    byCurrency?: BalanceByCurrency[];
  };
  cryptoWithdrawals?: {
    regular?: {
      count?: number;
      totalUSDT?: number;
      totalNGN?: number;
      byCurrency?: BalanceByCurrency[];
    };
    utility?: {
      count?: number;
      totalNGN?: number;
      byType?: BalanceByType[];
    };
  };
  cryptoSwaps?: {
    count?: number;
    byPair?: BalanceByPair[];
  };
  naira?: {
    deposits?: { count?: number; totalNGN?: number };
    withdrawals?: { count?: number; totalNGN?: number };
    swaps?: { count?: number; totalNGN?: number };
    utility?: {
      count?: number;
      totalNGN?: number;
      byType?: BalanceByType[];
    };
  };
  giftCards?: {
    count?: number;
    byCard?: BalanceByCard[];
  };
  payouts?: {
    count?: number;
    totalNGN?: number;
    bySource?: BalanceBySource[];
    byStatus?: BalanceByStatus[];
  };
};

const FIAT_KEYS = new Set(["ngn", "eur", "usd"]);

const DISPLAY_ORDER = [
  ...assetLogo.map((a) => a.name.toLowerCase()),
  ...Array.from(FIAT_KEYS),
];

/** Flat numeric balances only — excludes nested objects such as `hostcapital`. */
export function flatCurrentBalances(
  currentBalances: Record<string, unknown> | undefined
): Record<string, number> {
  if (!currentBalances) return {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(currentBalances)) {
    if (typeof value === "object" && value !== null) continue;
    if (typeof value === "number" && !Number.isNaN(value)) {
      out[key.toLowerCase()] = value;
    }
  }
  return out;
}

export function holdingBalanceEntries(
  currentBalances: Record<string, unknown> | undefined
): Array<{ key: string; value: number }> {
  const flat = flatCurrentBalances(currentBalances);
  const displayable = new Set(DISPLAY_ORDER);

  return Object.entries(flat)
    .filter(([key, value]) => displayable.has(key) && value >= 0)
    .sort(([a], [b]) => {
      const ai = DISPLAY_ORDER.indexOf(a);
      const bi = DISPLAY_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .map(([key, value]) => ({ key, value }));
}

export function formatHoldingBalanceAmount(key: string, value: number): string {
  const symbol = key.toUpperCase();
  if (key === "ngn") return `₦ ${formatAmount(value)}`;
  if (key === "eur") return `€ ${formatAmount(value)}`;
  if (key === "usd") return `$ ${formatAmount(value)}`;
  return `${formatAmount(value)} ${symbol}`;
}

export function formatSummaryNgn(value: number | undefined): string {
  if (value == null) return "—";
  return `₦ ${formatAmount(value)}`;
}

export function formatSummaryUsdt(value: number | undefined): string {
  if (value == null) return "—";
  return `${formatAmount(value)} USDT`;
}

export function cryptoWithdrawalCount(
  withdrawals: UserBalanceSummary["cryptoWithdrawals"]
): number {
  return (
    (withdrawals?.regular?.count ?? 0) + (withdrawals?.utility?.count ?? 0)
  );
}

export function nairaActivityCount(
  naira: UserBalanceSummary["naira"]
): number {
  if (!naira) return 0;
  return (
    (naira.deposits?.count ?? 0) +
    (naira.withdrawals?.count ?? 0) +
    (naira.swaps?.count ?? 0) +
    (naira.utility?.count ?? 0)
  );
}

/** Merge duplicate currency rows that differ only by case. */
export function mergeCurrencyBreakdown(
  items: BalanceByCurrency[] | undefined
): Array<{ currency: string; count: number; totalAmount: number; totalUSDT: number; totalNGN: number }> {
  if (!items?.length) return [];

  const map = new Map<
    string,
    { currency: string; count: number; totalAmount: number; totalUSDT: number; totalNGN: number }
  >();

  for (const item of items) {
    const currency = (item.currency ?? "—").toUpperCase();
    const existing = map.get(currency) ?? {
      currency,
      count: 0,
      totalAmount: 0,
      totalUSDT: 0,
      totalNGN: 0,
    };
    existing.count += item.count ?? 0;
    existing.totalAmount += item.totalAmount ?? 0;
    existing.totalUSDT += item.totalUSDT ?? 0;
    existing.totalNGN += item.totalNGN ?? 0;
    map.set(currency, existing);
  }

  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export function sortByCount<T extends { count?: number }>(items: T[] | undefined): T[] {
  return [...(items ?? [])].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
}
